import {
  Body,
  MenuItem,
  Sticker,
  isDualVariantIcon,
  useTheme,
} from "@biom3/react";
import { useTimeCountdown } from "@ui-kit/hooks";
import { motion } from "motion/react";
import { useEffect, useMemo } from "react";
import { BadgeContent } from "./BadgeContent";
import type { QuestKey, QuestState } from "./types";

type QuestItemProps = {
  quest: QuestState;
  index: number;
  nextQuestIndex: number;
  locked: boolean;
  mainQuest?: boolean;
  onQuestClick: (quest: QuestState) => void;
  onQuestLockTimerExpired?: (quest: QuestState) => void;
};

// here
export const isQuestCompletedForDay = (quest: QuestState | undefined) => {
  if (!quest || !quest.completed || !quest.completedAt || !quest.recurring) {
    return false;
  }

  const { recurringValidFrom, recurringRefreshPeriodDays } = quest;

  if (
    recurringValidFrom &&
    recurringRefreshPeriodDays !== undefined &&
    recurringRefreshPeriodDays > 0
  ) {
    const now = new Date();
    const validFromDate = new Date(recurringValidFrom);
    const periodMs = recurringRefreshPeriodDays * 24 * 60 * 60 * 1000;

    const timeSinceValidFrom = now.getTime() - validFromDate.getTime();
    const periodsSinceValidFrom = Math.max(
      0,
      Math.floor(timeSinceValidFrom / periodMs),
    );
    const currentPeriodStart = new Date(
      validFromDate.getTime() + periodsSinceValidFrom * periodMs,
    );

    return quest.completedAt.getTime() >= currentPeriodStart.getTime();
  }

  // fallback - default to old method for legacy quests
  const now = new Date();
  return (
    quest.completedAt.getUTCFullYear() === now.getUTCFullYear() &&
    quest.completedAt.getUTCMonth() === now.getUTCMonth() &&
    quest.completedAt.getUTCDate() === now.getUTCDate()
  );
};

export const QuestItem = ({
  quest,
  index,
  nextQuestIndex,
  locked,
  mainQuest,
  onQuestClick,
  onQuestLockTimerExpired,
}: QuestItemProps) => {
  const { base } = useTheme();
  const enabled = mainQuest || (index === nextQuestIndex && !locked);
  const isRecurringQuest =
    quest.recurring && quest.completionCount !== undefined;
  const notAllowed =
    (quest.completed && !isRecurringQuest) ||
    quest.locked ||
    !enabled ||
    locked;

  const getMenuItemText = (quest: QuestState) => {
    if (quest.locked && enabled) {
      return `Unlock quest in ${timeUntilUnlocked}`;
    }
    if (quest.locked) {
      return "Mystery Quest";
    }
    return quest.label;
  };

  const getMenuCaption = (quest: QuestState) => {
    if (quest.locked) {
      return "Come back soon to reveal";
    }
    return quest.caption;
  };

  const preventDefaultQuestKeys: QuestKey[] = [
    "discord-v2",
    "x-v2",
    "x-share-validation",
    "x-post-tweet-validation",
    "x-like-and-retweet-validation",
    "telegram-bot-connect",
    "social-engagement",
    "steam",
  ];

  const onClick = (e: { preventDefault: () => void }) => {
    if (preventDefaultQuestKeys.includes(quest.key)) {
      e.preventDefault();
    }
    onQuestClick(quest);
  };
  const { formatted: timeUntilUnlocked, msLeft } = useTimeCountdown(
    quest.lockedUntil,
  );
  const { formatted: timeUntilExpiration } = useTimeCountdown(quest.expiresAt);

  // When the countdown expires, call the onUnlock callback if provided.
  useEffect(() => {
    if (
      quest.locked &&
      msLeft !== undefined &&
      msLeft <= 0 &&
      onQuestLockTimerExpired
    ) {
      onQuestLockTimerExpired(quest);
    }
  }, [msLeft, quest, onQuestLockTimerExpired]);

  const questItemSx = useMemo(
    () => ({
      // @NOTE: over-ride default <button> bg colors
      bg: "transparent",
      pointerEvents: notAllowed ? "none" : "auto",
      cursor: notAllowed ? "not-allowed" : "pointer",
      "&.MenuItem--emphasized": {
        bg: "base.color.translucent.emphasis.500",
      },
      order: enabled ? 0 : quest.hidden ? 2 : 1,
      transitionDelay: base.motion.normal.fast.cssDuration,
    }),
    [enabled, quest.hidden, notAllowed, base.motion],
  );

  const fadedOutSx = useMemo(
    () => ({
      opacity: quest.hidden && !isRecurringQuest ? 0.5 : 1,
      transitionProperty: "opacity",
      transitionDuration: base.motion.normal.gentle.cssDuration,
      transitionTimingFunction: base.motion.normal.gentle.cssEase,
      transitionDelay: base.motion.normal.gentle.cssDuration,
    }),
    [quest.hidden, isRecurringQuest, base.motion.normal.gentle],
  );

  const completionCount = quest.completionCount && quest.completionCount > 0;
  const recurringBadgeRequired = Boolean(quest.recurring && completionCount);
  const recurringQuestCompletedToday =
    quest.recurring && isQuestCompletedForDay(quest);
  const showAsCompleted =
    (quest.completed && !quest.recurring) || recurringQuestCompletedToday;

  return (
    <MenuItem
      testId="QuestItem"
      emphasized={mainQuest ? false : enabled}
      motionProfile="fast"
      size={mainQuest ? "xSmall" : enabled ? "small" : "xSmall"}
      sx={questItemSx}
      rc={
        quest.type === "link" ||
        (quest.key === "in-game" && !!quest.linkUrl) ? (
          <motion.a
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noreferrer"
            onClick={onClick}
            href={
              quest.key === "discord-v2" || quest.key === "x-v2"
                ? "#"
                : (quest.linkUrl ?? "#")
            }
            layout
            exit={{ opacity: 0 }}
          />
        ) : (
          <motion.button
            onClick={
              (quest.completed && !isRecurringQuest) || !enabled
                ? undefined
                : onClick
            }
            disabled={notAllowed}
            layout
            exit={{ opacity: 0 }}
          />
        )
      }
    >
      <Sticker position={{ x: "right", y: "bottom" }}>
        <Sticker.FramedIcon
          icon="Lock"
          variant="bold"
          circularFrame
          sx={{
            w: enabled ? "base.icon.size.300" : "base.icon.size.200",
            h: enabled ? "base.icon.size.300" : "base.icon.size.200",
            fill: "base.color.fixed.white.1000",
            visibility: quest.locked ? "visible" : "hidden",
          }}
        />
        <Sticker.FramedIcon
          icon="ImxRewards"
          variant="bold"
          emphasized
          sx={{
            w: "base.icon.size.200",
            h: "base.icon.size.200",
            fill: "base.color.fixed.black.1000",
            background: "base.gradient.1",
            visibility: quest.perpetualRewardsEligible ? "visible" : "hidden",
          }}
        />
        <MenuItem.FramedIcon
          {...(isDualVariantIcon(quest.icon)
            ? { icon: quest.icon, variant: quest.iconVariant }
            : { icon: quest.icon })}
          sx={fadedOutSx}
        />
      </Sticker>
      <MenuItem.Label
        sx={{
          ...fadedOutSx,
          WebkitLineClamp: 10,
          transitionDelay: base.motion.normal.gentle.cssDuration,
          textDecoration:
            quest.completed && !isRecurringQuest ? "line-through" : "none",
        }}
      >
        {getMenuItemText(quest)}
      </MenuItem.Label>
      {!locked && (
        <MenuItem.Caption
          sx={{
            transitionDelay: base.motion.normal.gentle.cssDuration,
            overflow: "visible",
            WebkitLineClamp: "none",
          }}
        >
          {getMenuCaption(quest)}
          {quest.perpetualRewardsEligible &&
            getMenuCaption(quest).length > 0 && <br />}
          {quest.perpetualRewardsEligible && (
            <Body size="xSmall" sx={{ color: "base.gradient.1" }}>
              Eligible for Perpetual Rewards
            </Body>
          )}
        </MenuItem.Caption>
      )}
      {quest.status === "in-progress" &&
      !quest.ruleModal &&
      quest.key !== "in-game" ? (
        <MenuItem.IntentIcon
          icon="Loading"
          sx={{
            fill: "base.color.text.body.secondary",
          }}
        />
      ) : (
        <MenuItem.IntentIcon
          icon={
            showAsCompleted
              ? "TickWithCircle"
              : quest.locked && enabled
                ? "Lock"
                : "ChevronForward"
          }
          variant={showAsCompleted || quest.locked ? "bold" : "regular"}
          sx={{
            fill: showAsCompleted
              ? "base.color.status.success.bright"
              : "base.color.text.body.secondary",
          }}
        />
      )}
      {recurringBadgeRequired && ( // here
        <MenuItem.Badge
          variant={recurringQuestCompletedToday ? "success" : "dark"}
          sx={{ transitionDelay: base.motion.normal.gentle.cssDuration }}
          badgeContent={`x${quest.completionCount}`}
        />
      )}

      {!!quest.badgeTemplate && (
        <MenuItem.Badge
          variant={showAsCompleted ? "success" : "dark"}
          sx={{
            transitionDelay: base.motion.normal.gentle.cssDuration,
            py: "base.spacing.x1",
            px: "base.spacing.x2",
            borderRadius: "base.borderRadius.x3",
          }}
          badgeContent={
            <BadgeContent
              badgeTemplate={quest.badgeTemplate}
              badgeIcon={quest.badgeIcon}
              increase={quest.increase}
            />
          }
        />
      )}
    </MenuItem>
  );
};
